import { useCallback, useMemo } from 'react';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFetchWithCache } from '../common/utils/useFetchWithCache.tsx';
import type {
	EmailSettingsResponse,
	HipaaComplianceResponse,
	useFetchSettingsReturnType,
} from '../common/types.tsx';

const getHipaaComplianceStatus = (): Promise<HipaaComplianceResponse> =>
	performGetRequest('/rest/internal/latest/compliance/status/HIPAA');

const getEmailSettings = (): Promise<EmailSettingsResponse> =>
	performGetRequest('/rest/jira-mail-plugin/1.0/outgoingMail/config');

export const useFetchSettings = (): useFetchSettingsReturnType => {
	const callback = useCallback(
		() => Promise.allSettled([getHipaaComplianceStatus(), getEmailSettings()]),
		[],
	);

	const { fetch, status } = useFetchWithCache(callback, 'fetchSettings');
	const result = useMemo<useFetchSettingsReturnType['result']>(() => {
		if (status === 'loading' || status === 'idle') {
			return { status: 'loading', payload: undefined };
		}
		// This should never happen but need to cater for type
		if (status === 'error') {
			return { status: 'error', payload: undefined };
		}
		const [hipaaResult, emailResult] = status;
		let shouldError = true;
		status.forEach((s) => {
			if (s.status === 'rejected') {
				shouldError = shouldError && true;
				fireErrorAnalytics({
					meta: {
						id: 'fetchSettings',
						packageName: 'jiraShareDialog',
						teamName: 'jira-warepil',
					},
					error: new Error(s.reason),
				});
				return;
			}
			shouldError = shouldError && false;
		});
		if (shouldError) {
			return { status: 'error', payload: undefined };
		}
		return {
			status: 'success',
			payload: {
				hipaaComplianceStatus: hipaaResult.status === 'fulfilled' ? hipaaResult.value : 'error',
				emailSettings: emailResult.status === 'fulfilled' ? emailResult.value : 'error',
			},
		};
	}, [status]);

	return {
		fetch,
		result,
	};
};
